import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Flex, Text, SimpleGrid, Spinner, Textarea, Tooltip, useToast } from '@chakra-ui/react';
import { FiLock } from 'react-icons/fi';
import xhr from 'xhr';

/**
 * Panel del modal "Matricular Materias" que muestra las cantidades pagadas
 * para la matrícula del estudiante en el programa (semester-based).
 *
 * Tres estados por cantidad:
 *   - Pagada por pago vinculado — morado con candado, NO editable desde aquí.
 *   - Ajuste manual de cartera — morado con badge "manual", editable/quitable.
 *   - Libre — gris, clickeable para marcarla como ajuste manual.
 *
 * Cuando Cindy toca algo, se muestra el textarea de razón (obligatoria) y el
 * botón "Guardar ajuste". Al guardar llama PUT /payments/manual-ordinals con
 * el conjunto NUEVO de ajustes manuales (reemplaza el anterior).
 */
export default function PaidQuantitiesPanel({
  studentId,
  programId,
  enrollmentType,
  activatedCount,
  subjectsTotal,
}) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(subjectsTotal || 0);
  const [enrollmentId, setEnrollmentId] = useState(null);
  const [paidByPayments, setPaidByPayments] = useState(new Set());   // por pagos vinculados
  const [savedManual, setSavedManual] = useState(new Set());         // ajuste manual guardado en BD
  const [draftManual, setDraftManual] = useState(new Set());         // ajuste manual pendiente (local)
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!studentId || !programId || !enrollmentType) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        enrollment_type: enrollmentType,
        student_id: String(studentId),
        program_id: String(programId),
      });
      const res = await xhr.get(`/payments/manual-link/candidates?${params.toString()}`);
      const payload = res?.data?.data || {};
      setTotal(payload.meta?.subjectCount || subjectsTotal || 0);

      // Cantidades por pagos vinculados (unión de paid_subject_numbers).
      const byPay = new Set();
      for (const p of (payload.payments || [])) {
        const nums = Array.isArray(p.paid_subject_numbers) ? p.paid_subject_numbers : [];
        for (const n of nums) {
          const v = Number(n);
          if (Number.isInteger(v) && v >= 1) byPay.add(v);
        }
      }
      setPaidByPayments(byPay);

      // La matrícula sobre la que aplicamos el ajuste manual: la más reciente
      // (ya viene ordenada por createdAt asc, tomamos la última). El picker
      // funciona a nivel del programa, no de la matrícula, así que si el
      // estudiante tiene varias, el ajuste va a la más reciente. En el 99% de
      // los casos hay una sola.
      const enrs = Array.isArray(payload.enrollments) ? payload.enrollments : [];
      const target = enrs[enrs.length - 1] || null;
      setEnrollmentId(target?.id ?? null);
      const manualSet = new Set(
        (target?.additionalPaidOrdinals || [])
          .map((n) => Number(n))
          .filter((n) => Number.isInteger(n) && n >= 1)
      );
      setSavedManual(manualSet);
      setDraftManual(new Set(manualSet));
      setNote('');
    } catch (err) {
      console.error('PaidQuantitiesPanel load:', err);
    } finally {
      setLoading(false);
    }
  }, [studentId, programId, enrollmentType, subjectsTotal]);

  useEffect(() => { load(); }, [load]);

  const toggle = (n) => {
    if (paidByPayments.has(n)) return; // bloqueada
    setDraftManual((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
  };

  // Cambios pendientes: diferencia entre savedManual y draftManual.
  const hasChanges = (
    draftManual.size !== savedManual.size ||
    [...draftManual].some((n) => !savedManual.has(n))
  );
  const canSave = hasChanges && (draftManual.size === 0 || note.trim().length >= 10);

  const handleSave = async () => {
    if (!enrollmentId || !enrollmentType || !canSave) return;
    setSaving(true);
    try {
      await xhr.put('/payments/manual-ordinals', {
        data: {
          enrollment_type: enrollmentType,
          enrollment_id: enrollmentId,
          ordinals: [...draftManual].sort((a, b) => a - b),
          note: draftManual.size === 0 ? undefined : note.trim(),
        },
      });
      toast({ title: 'Ajuste guardado', status: 'success', position: 'bottom' });
      await load();
    } catch (err) {
      toast({
        title: 'No se pudo guardar el ajuste',
        description: err?.response?.data?.error?.message || err?.message,
        status: 'error',
        position: 'bottom',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setDraftManual(new Set(savedManual));
    setNote('');
  };

  if (!total) return null;
  const paidCount = new Set([...paidByPayments, ...draftManual]).size;
  const byPayCount = paidByPayments.size;
  const manualCount = [...draftManual].filter((n) => !paidByPayments.has(n)).length;
  const items = Array.from({ length: total }, (_, i) => i + 1);
  const canEdit = !!enrollmentId;

  return (
    <Box marginY="12px">
      <Flex
        wrap="wrap"
        align="baseline"
        gap="14px"
        bg="#FBF9F7"
        border="1px solid #E4DFE8"
        borderRadius="8px"
        padding="12px 16px"
        marginBottom="10px"
        fontSize="13px"
        color="gray.700"
      >
        <Text><Text as="b" fontFamily="mono" color="gray.800">{total}</Text> materias del programa</Text>
        <Text color="gray.400">·</Text>
        <Text>
          <Text as="b" fontFamily="mono" color="gray.800">{paidCount}</Text> pagadas
          {(byPayCount > 0 || manualCount > 0) && (
            <Text as="span" fontSize="11px" color="gray.500" fontStyle="italic" ml={1}>
              (<Text as="span" color="#5B21B6" fontStyle="normal">{byPayCount} por pago</Text>
              {manualCount > 0 && <> · <Text as="span" color="#B45309" fontStyle="normal">{manualCount} manual{manualCount === 1 ? '' : 'es'}</Text></>})
            </Text>
          )}
        </Text>
        <Text color="gray.400">·</Text>
        <Text><Text as="b" fontFamily="mono" color="gray.800">{activatedCount ?? 0}</Text> activadas</Text>
      </Flex>

      <Box border="1px solid #E4DFE8" borderRadius="10px" overflow="hidden">
        <Flex
          bg="#F5F3FF"
          borderBottom="1px solid #DDD6FE"
          padding="10px 16px"
          align="center"
          justify="space-between"
        >
          <Text fontSize="11px" fontWeight="700" color="#5B21B6" letterSpacing="0.08em" textTransform="uppercase">
            ¿Qué tiene pagado el estudiante?
          </Text>
          {canEdit && (
            <Flex align="center" gap={1.5} fontSize="10px" color="gray.500" textTransform="uppercase" fontWeight="600" letterSpacing="0.06em">
              <Box w="6px" h="6px" borderRadius="50%" bg="#0F766E" />
              <Text>Editable</Text>
            </Flex>
          )}
        </Flex>

        <Box padding="14px 16px" bg="#FBF9F7">
          {loading ? (
            <Flex justify="center" py={2}><Spinner size="sm" /></Flex>
          ) : (
            <SimpleGrid minChildWidth="120px" spacing="6px">
              {items.map((n) => {
                const isPaidByPay = paidByPayments.has(n);
                const isDraft = draftManual.has(n);
                const wasSavedManual = savedManual.has(n);
                const isManualNew = isDraft && !wasSavedManual && !isPaidByPay;
                const isManualRemoved = wasSavedManual && !isDraft;

                let styles;
                let badge = null;
                let leftIcon = null;
                let tooltip = '';
                if (isPaidByPay) {
                  styles = { border: '1px solid #574feb', bg: '#F5F3FF', color: '#5B21B6', fontWeight: '600', cursor: 'not-allowed' };
                  leftIcon = <Box color="#574feb"><FiLock size="10" /></Box>;
                  tooltip = 'Cubierta por un pago vinculado. Edítala desde el modal de vinculación del reporte.';
                } else if (isDraft && wasSavedManual) {
                  styles = { border: '1px solid #F59E0B', bg: '#FEF3C7', color: '#B45309', fontWeight: '600', cursor: 'pointer' };
                  badge = 'manual';
                  tooltip = 'Ajuste manual guardado. Clic para desmarcar.';
                } else if (isManualNew) {
                  styles = { border: '1.5px dashed #F59E0B', bg: '#FFFBEB', color: '#B45309', fontWeight: '600', cursor: 'pointer' };
                  badge = 'nuevo';
                  tooltip = 'Ajuste manual pendiente de guardar.';
                } else if (isManualRemoved) {
                  styles = { border: '1.5px dashed #B45309', bg: 'white', color: 'gray.400', fontWeight: '500', cursor: 'pointer', textDecoration: 'line-through' };
                  tooltip = 'Ajuste manual pendiente de quitar.';
                } else {
                  styles = { border: '1px solid #E4DFE8', bg: 'white', color: 'gray.700', fontWeight: '500', cursor: 'pointer' };
                }

                const chip = (
                  <Flex
                    key={n}
                    align="center"
                    gap="8px"
                    padding="8px 10px"
                    borderRadius="5px"
                    fontSize="12px"
                    position="relative"
                    onClick={() => toggle(n)}
                    _hover={!isPaidByPay ? { transform: 'translateY(-1px)' } : {}}
                    transition="transform 0.1s ease"
                    {...styles}
                  >
                    <Flex
                      w="16px"
                      h="16px"
                      borderRadius="3px"
                      border={isPaidByPay
                        ? '1.5px solid #574feb'
                        : (isDraft && wasSavedManual)
                          ? '1.5px solid #F59E0B'
                          : isManualNew
                            ? '1.5px solid #F59E0B'
                            : '1.5px solid #7B7387'}
                      bg={isPaidByPay
                        ? '#574feb'
                        : (isDraft && wasSavedManual)
                          ? '#F59E0B'
                          : isManualNew
                            ? '#F59E0B'
                            : 'white'}
                      flexShrink={0}
                      align="center"
                      justify="center"
                    >
                      {(isPaidByPay || isDraft) && (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      )}
                    </Flex>
                    {leftIcon}
                    <Text as="span">Materia {n}</Text>
                    {badge && (
                      <Text
                        as="span"
                        position="absolute"
                        top="-6px"
                        right="-4px"
                        bg={isManualNew ? '#F59E0B' : '#F59E0B'}
                        color="white"
                        fontSize="8px"
                        fontWeight="700"
                        letterSpacing="0.05em"
                        textTransform="uppercase"
                        padding="1px 5px"
                        borderRadius="3px"
                      >
                        {badge}
                      </Text>
                    )}
                  </Flex>
                );

                return tooltip ? (
                  <Tooltip key={n} label={tooltip} fontSize="11px" placement="top" hasArrow>
                    {chip}
                  </Tooltip>
                ) : chip;
              })}
            </SimpleGrid>
          )}

          {hasChanges && canEdit && (
            <Box mt={4} p={3} bg="#FFFBEB" borderRadius="8px" borderLeft="3px solid #F59E0B">
              <Text fontSize="11px" fontWeight="700" color="#B45309" letterSpacing="0.06em" textTransform="uppercase" mb={2}>
                Razón del ajuste manual{draftManual.size > 0 && ' *'}
              </Text>
              {draftManual.size > 0 && (
                <>
                  <Textarea
                    size="sm"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Ej. el estudiante tiene un solo pago que cubrió ambas matrículas; se ajustan las cantidades faltantes"
                    bg="white"
                    minH="70px"
                  />
                  <Text fontSize="10px" color={note.trim().length >= 10 ? 'gray.500' : 'orange.600'} mt={1}>
                    Mínimo 10 caracteres · queda en audit log
                  </Text>
                </>
              )}
              {draftManual.size === 0 && savedManual.size > 0 && (
                <Text fontSize="12px" color="gray.700">
                  Vas a limpiar todos los ajustes manuales de esta matrícula.
                </Text>
              )}
              <Flex justify="flex-end" gap={2} mt={3}>
                <Button size="xs" variant="ghost" onClick={handleDiscard} isDisabled={saving}>
                  Descartar
                </Button>
                <Button
                  size="xs"
                  bg="#F59E0B"
                  color="white"
                  _hover={{ bg: '#D97706' }}
                  onClick={handleSave}
                  isLoading={saving}
                  isDisabled={!canSave || saving}
                >
                  {draftManual.size === 0 ? 'Limpiar ajuste' : `Guardar ajuste (${manualCount} manual${manualCount === 1 ? '' : 'es'})`}
                </Button>
              </Flex>
            </Box>
          )}

          {!hasChanges && !canEdit && (
            <Text fontSize="11px" color="gray.500" fontStyle="italic" mt="10px">
              El estudiante no tiene matrícula activa en este programa. Los ajustes se hacen sobre la matrícula existente.
            </Text>
          )}
        </Box>
      </Box>
    </Box>
  );
}
